import { toast } from 'sonner'
import { AlertTriangle } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDeleteList } from '@/entities/list'
import type { CrmListRow } from '@intone/shared/types'

interface DialogDeleteListProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: CrmListRow | null
}

export function DialogDeleteList({ open, onOpenChange, list }: DialogDeleteListProps) {
  const deleteList = useDeleteList()

  const handleDelete = async () => {
    if (!list) return

    try {
      await deleteList.mutateAsync(list.id)
      toast.success('List deleted')
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to delete list')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete List
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-body text-gray-600">
            Are you sure you want to delete this list?
          </p>

          <div className="bg-gray-50 rounded-lg p-3 text-body">
            <div>
              <span className="font-medium">Name:</span> {list?.name}
            </div>
            <div>
              <span className="font-medium">ID:</span> {list?.id}
            </div>
            <div>
              <span className="font-medium">Leads:</span> {list?.size.toLocaleString()}
            </div>
          </div>

          <p className="text-body text-red-600 font-medium">
            This action cannot be undone. All leads in this list will also be deleted.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteList.isPending}>
            {deleteList.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}